import React from "react";
import {AlertTriangle, CheckCircle2, Circle, Copy, Lightbulb, Mail, RefreshCw} from "lucide-react";
import {Link, useParams} from "react-router-dom";
import {Card, CardContent, CardHeader, CardTitle} from "../ui/card";
import {Button} from "../ui/button";
import {Cell} from "../project_conf/bidConfig_model";
import {USDtoValue} from "../fields/USDField";
import {rankBadge} from "../fields/feedback";

type BidChecklistProps = {
    bidAmount: number;
    fileCount: number;
    sheetData: Cell[][] | null;
    onGoToFiles: () => void;
};

type Item = {
    key: string;
    label: string;
    detail: string;
};

/**
 * Counts the cells the bidder is meant to fill in.
 * Convention for party: 0(false)=Developer, 1(true)=bidder.
 * */
export function sovProgress(sheetData: Cell[][] | null) {
    if (!sheetData) {
        return null;
    }
    const inputs = sheetData.flat().filter((c) => c && c.party);
    const filled = inputs.filter((c) => String(c.value ?? "").trim() !== "").length;
    return {filled: filled, total: inputs.length};
}

function ItemRow({item, done}: { item: Item, done: boolean }) {
    return <div className="flex gap-2">
        {done
            ? <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5 text-green-500"/>
            : <Circle className="h-4 w-4 shrink-0 mt-0.5 text-gray-300"/>
        }
        <div>
            <div className={"text-sm " + (done ? "font-medium" : "text-gray-500")}>
                {item.label}
            </div>
            {item.detail &&
                <div className="text-xs text-gray-400">{item.detail}</div>
            }
        </div>
    </div>
}

/**
 * The example rows shown under "After you submit". These are made up - they are
 * here so the bidder can see the shape of the feedback, not real numbers.
 * */
const EXAMPLE_ROWS = [
    {label: "Tons of rebar", value: "128", feedback: {rank: 2, o: 5}},
    {label: "Installation", value: "$34,000", feedback: {rank: 4, o: 5}},

];

function AfterYouSubmit() {
    return <Card>
        <CardHeader>
            <CardTitle className="text-base">After you submit</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">

             <p className="text-[11px] text-gray-600 mt-2 ">
                Fill out the Schedule of values to get feedback on each item.
            </p>
            <div className="rounded-md border border-gray-200 p-3">
                <div className="text-[11px] font-medium uppercase tracking-wide text-gray-400 mb-2">
                    Example SOV
                </div>
                {EXAMPLE_ROWS.map((row) => (
                    <div key={row.label}
                         className="flex items-center justify-between gap-2 py-1 border-b border-gray-100 last:border-0">
                        <span className="text-xs text-gray-500">{row.label}</span>
                        <span className="flex items-center gap-2">
                            <span className="text-xs">{row.value}</span>
                            {rankBadge(row.feedback)}
                        </span>
                    </div>
                ))}
            </div>


            <div className="mt-4 flex flex-col gap-3 text-xs text-gray-600">
                <div className="flex gap-2">
                    <Mail className="h-4 w-4 shrink-0 text-gray-400"/>
                    <span>An email lands whenever your rank moves.</span>
                </div>
                <div className="flex gap-2">
                    <RefreshCw className="h-4 w-4 shrink-0 text-gray-400"/>
                    <span>Check back often, the ladder moves as others revise.</span>
                </div>
                <div className="flex gap-2">
                    <Copy className="h-4 w-4 shrink-0 text-gray-400"/>
                    <span>Revise any time with <b>Copy</b>, it clones this bid so you edit
                        instead of retyping.</span>
                </div>
            </div>
        </CardContent>
    </Card>
}

/**
 * Informational rail shown to a builder while their bid is still a draft.
 * Nothing here is saved - it all reads off state the page already holds.
 * */
export function BidChecklist({bidAmount, fileCount, sheetData, onGoToFiles}: BidChecklistProps) {
    const {project_id} = useParams<string>();
    const sov = sovProgress(sheetData);

    const items: Item[] = [
        {
            key: "form",
            label: "Bid form",
            detail: bidAmount > 0 ? USDtoValue(bidAmount) + " entered" : "No amount yet",
        },
        {
            key: "proposal",
            label: "Proposal attached",
            detail: fileCount > 0
                ? fileCount + (fileCount === 1 ? " file attached" : " files attached")
                : "",
        },
    ];
    const status: { [key: string]: boolean } = {
        form: bidAmount > 0,
        proposal: fileCount > 0,
    };

    // no bid template on this project - then there is no SOV to fill.
    if (sov) {
        items.push({
            key: "sov",
            label: "Schedule of Values",
            detail: sov.filled + " of " + sov.total + " entered",
        });
        status.sov = sov.filled > 0;
    }

    const doneCount = items.filter((i) => status[i.key]).length;
    const firstOpen = items.find((i) => !status[i.key]);

    return <div className="flex flex-col gap-4">
        <Card>
            <CardHeader>
                <CardTitle className="flex justify-between items-center">
                    <div className="text-base">Bid checklist</div>
                    <div className="text-sm font-normal text-gray-400">
                        {doneCount} / {items.length}
                    </div>
                </CardTitle>
                <div className="flex gap-1 pt-1">
                    {items.map((i) => (
                        <div
                            key={i.key}
                            className={"h-1 flex-1 rounded-full " + (
                                status[i.key]
                                    ? "bg-green-500"
                                    : firstOpen && firstOpen.key === i.key
                                        ? "bg-amber-400"
                                        : "bg-gray-200"
                            )}
                        />
                    ))}
                </div>
            </CardHeader>
            <CardContent className="pt-0 flex flex-col gap-4">
                {items.map((item) => (
                    <div key={item.key}>
                        <ItemRow item={item} done={status[item.key]}/>
                        {item.key === "proposal" && !status.proposal &&
                            <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-3">
                                <div className="flex gap-2">
                                    <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-amber-500"/>
                                    <span className="text-xs text-amber-800">
                                        Your proposal is required for leveling. Without it
                                        your scope reads as unqualified.
                                    </span>
                                </div>

                            </div>
                        }
                    </div>
                ))}
                {/* optional - deliberately left out of the count above */}
                <div className=" pt-3 flex gap-2">
                    <Lightbulb className="h-4 w-4 shrink-0 mt-0.5 text-gray-300"/>
                    <div>
                        <div className="text-sm text-gray-500">
                            VE ideas

                        </div>
                        <div className="text-xs text-gray-400">
                            A design change you would make gets read next to your number.
                        </div>
                        <Link
                            to={`/project/${project_id}/ve`}
                            className="text-xs text-blue-600 hover:underline"
                        >
                            Open the VE tab →
                        </Link>
                    </div>
                </div>

                <div className="text-xs text-gray-400 border-t pt-3">
                    {doneCount === items.length
                        ? "All set - you can submit."
                        : doneCount + " of " + items.length + " done"
                        + (firstOpen ? " - " + firstOpen.label.toLowerCase() + " still missing" : "")}
                </div>
            </CardContent>
        </Card>

        <AfterYouSubmit/>
    </div>
}

export default BidChecklist;
